/**
 * Product-update email list, client side.
 *
 * Two paths, deliberately different:
 *  - Anonymous (subscribe box): POSTs to /api/email/subscribe, which sends a
 *    confirmation link. Nothing is stored until that link is tapped.
 *  - Signed in (/account page toggle): calls the RPCs directly, because the
 *    session already proves the address belongs to the person flipping it.
 *
 * The RPCs are SECURITY DEFINER and the table has no policies, so this module
 * is the whole client surface - there is no way to read the list from a
 * browser.
 */
import { SUPABASE_ANON_KEY, SUPABASE_URL, supabase } from './supabase'

/**
 * Calls one of the two subscription RPCs with an EXPLICIT bearer token instead
 * of whatever session the shared client holds at the moment it runs.
 *
 * The signup prompt needs this: `supabase.rpc()` resolves its token
 * asynchronously, so an account switch in another tab during that lookup can
 * land the write on an account that was never shown the prompt. A caller that
 * captured the token up front cannot be redirected that way. iOS has always
 * called these two RPCs exactly like this.
 *
 * Returns the RPC's text result, or null for any failure.
 */
async function rpcWithToken(
  name: string,
  body: Record<string, unknown>,
  accessToken: string,
): Promise<string | null> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return null
  try {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${name}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(body),
    })
    if (!res.ok) return null
    const value: unknown = await res.json()
    return typeof value === 'string' ? value : null
  } catch {
    return null
  }
}

export type SubscriptionStatus = 'confirmed' | 'unsubscribed' | 'none'

/** Same shape the server enforces; keeps an obvious typo out of the request. */
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
export function isPlausibleEmail(value: string): boolean {
  const v = value.trim()
  return v.length > 0 && v.length <= 254 && EMAIL_RE.test(v)
}

export type SubscribeResult = 'sent' | 'invalid' | 'error'

/** Anonymous opt-in. 'sent' means a confirmation mail was requested, never
 * that the address is on the list - the endpoint deliberately will not say. */
export async function requestSubscription(email: string, source: string): Promise<SubscribeResult> {
  if (!isPlausibleEmail(email)) return 'invalid'
  try {
    const res = await fetch('/api/email/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase(), source }),
    })
    if (res.status === 400) return 'invalid'
    return res.ok ? 'sent' : 'error'
  } catch {
    return 'error'
  }
}

/**
 * Current status for the signed-in user. Null means we could not tell.
 *
 * `accessToken` pins the answer to one session; without it the shared client's
 * current session is used, which is what the /account toggle wants.
 */
export async function getMySubscription(accessToken?: string): Promise<SubscriptionStatus | null> {
  let data: unknown
  if (accessToken) {
    data = await rpcWithToken('my_email_subscription', {}, accessToken)
    if (data === null) return null
  } else {
    if (!supabase) return null
    const result = await supabase.rpc('my_email_subscription')
    if (result.error) return null
    data = result.data
  }
  return data === 'confirmed' || data === 'unsubscribed' ? data : 'none'
}

/** Returns the new status, or null when the write failed (caller reverts). */
export async function setMySubscription(
  subscribed: boolean,
  accessToken?: string,
): Promise<SubscriptionStatus | null> {
  let data: unknown
  if (accessToken) {
    data = await rpcWithToken('set_my_email_subscription', { p_subscribed: subscribed }, accessToken)
    if (data === null) return null
  } else {
    if (!supabase) return null
    const result = await supabase.rpc('set_my_email_subscription', { p_subscribed: subscribed })
    if (result.error) return null
    data = result.data
  }
  // 'no_email' comes back for an account with no address on file; treat it as
  // not subscribed rather than inventing a success the user cannot verify.
  return data === 'confirmed' ? 'confirmed' : data === 'unsubscribed' ? 'unsubscribed' : 'none'
}
